import { useCallback, useEffect, useMemo, useState } from "react";

import { useRoomSync, type SubmitOpResult } from "../RoomSyncProvider";
import { newUuid } from "../syncReducer";
import { authorHistory, liveShapeIds, makeRedo, makeUndo } from "./opLog";
import type { NormPointTuple, OpDraft, Shape, ShapeGeometry, StrokeWidth, Tool } from "./shapes";
import { SHAPE_COLORS } from "./shapes";

/**
 * useAnnotationSession - the editing layer on top of opLog/useRoomSync (D-08, D-09, D-10, D-12).
 *
 * Every action goes through sync.submitOp, so ops are optimistic, broadcast and POSTed exactly
 * the same way regardless of which action produced them. Undo/redo are derived from the log
 * itself (makeUndo/makeRedo/authorHistory, 05-06) rather than a separate undo stack.
 */

export type AnnotationTool = Tool | "pointer" | "select";

const OVERSIZE_MESSAGE = "That drawing is too large to share. Try a shorter stroke.";
const NOT_PERMITTED_MESSAGE = "Drawing has not been enabled for you.";

export type AnnotationSession = {
  tool: AnnotationTool;
  setTool: (tool: AnnotationTool) => void;
  color: string;
  setColor: (color: string) => void;
  width: StrokeWidth;
  setWidth: (width: StrokeWidth) => void;
  selectedShapeId: string | null;
  setSelectedShapeId: (id: string | null) => void;
  frameId: string | null;
  shapes: Shape[];
  canDraw: boolean;
  canModify: (shape: Shape) => boolean;
  create: (geom: { tool: Tool; points: NormPointTuple[]; color: string; width: StrokeWidth }) => void;
  move: (shapeId: string, before: ShapeGeometry, after: ShapeGeometry) => void;
  restyleSelected: (change: { color?: string; width?: StrokeWidth }) => void;
  deleteSelected: () => void;
  clearAll: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  message: string | null;
  clearMessage: () => void;
};

export function useAnnotationSession(): AnnotationSession {
  const sync = useRoomSync();
  const frameId = sync.room?.activeFrame?.frameId ?? null;
  const canDraw = frameId != null && sync.canAnnotate;
  const shapesFor = sync.shapesFor;
  const shapes = useMemo(() => (frameId ? shapesFor(frameId) : []), [frameId, shapesFor]);

  const [tool, setTool] = useState<AnnotationTool>("pointer");
  const [color, setColorState] = useState<string>(SHAPE_COLORS[0]);
  const [width, setWidthState] = useState<StrokeWidth>(4);
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Drawing only makes sense on a frozen frame the caller may annotate (D-07/D-11): switching
  // frames, or losing permission, always drops back to the pointer tool and any selection.
  useEffect(() => {
    setTool("pointer");
    setSelectedShapeId(null);
  }, [frameId, canDraw]);

  const canModify = useCallback(
    (shape: Shape): boolean => sync.role === "worker" || shape.authorIdentity === sync.localIdentity,
    [sync.role, sync.localIdentity],
  );

  const submitOp = sync.submitOp;
  const submit = useCallback(
    (draft: OpDraft): SubmitOpResult => {
      const result = submitOp(draft);
      if (!result.ok) {
        setMessage(result.reason === "oversize" ? OVERSIZE_MESSAGE : NOT_PERMITTED_MESSAGE);
      }
      return result;
    },
    [submitOp],
  );

  const create = useCallback(
    (geom: { tool: Tool; points: NormPointTuple[]; color: string; width: StrokeWidth }) => {
      if (!frameId) {
        return;
      }
      submit({
        frameId,
        kind: "ShapeAdd",
        payload: { shape: { id: newUuid(), tool: geom.tool, points: geom.points, color: geom.color, width: geom.width } },
        undoOf: null,
        redoOf: null,
      });
    },
    [frameId, submit],
  );

  const move = useCallback(
    (shapeId: string, before: ShapeGeometry, after: ShapeGeometry) => {
      if (!frameId) {
        return;
      }
      submit({ frameId, kind: "ShapeUpdate", payload: { shapeId, before, after }, undoOf: null, redoOf: null });
    },
    [frameId, submit],
  );

  const restyleSelected = useCallback(
    (change: { color?: string; width?: StrokeWidth }) => {
      if (!frameId || !selectedShapeId) {
        return;
      }
      const shape = shapes.find((s) => s.id === selectedShapeId);
      if (!shape || !canModify(shape)) {
        return;
      }
      const before: ShapeGeometry = { points: shape.points, color: shape.color, width: shape.width };
      const after: ShapeGeometry = {
        points: shape.points,
        color: change.color ?? shape.color,
        width: change.width ?? shape.width,
      };
      submit({ frameId, kind: "ShapeUpdate", payload: { shapeId: selectedShapeId, before, after }, undoOf: null, redoOf: null });
    },
    [frameId, selectedShapeId, shapes, canModify, submit],
  );

  // Setting the colour or width while a modifiable shape is selected also restyles it, so the
  // toolbar's swatches double as an edit for the current selection.
  const setColor = useCallback(
    (next: string) => {
      setColorState(next);
      restyleSelected({ color: next });
    },
    [restyleSelected],
  );

  const setWidth = useCallback(
    (next: StrokeWidth) => {
      setWidthState(next);
      restyleSelected({ width: next });
    },
    [restyleSelected],
  );

  const deleteSelected = useCallback(() => {
    if (!frameId || !selectedShapeId) {
      return;
    }
    const shape = shapes.find((s) => s.id === selectedShapeId);
    if (!shape || !canModify(shape)) {
      return;
    }
    const result = submit({
      frameId,
      kind: "ShapeDelete",
      payload: { shapeIds: [selectedShapeId] },
      undoOf: null,
      redoOf: null,
    });
    if (result.ok) {
      setSelectedShapeId(null);
    }
  }, [frameId, selectedShapeId, shapes, canModify, submit]);

  // Worker only (D-08): clears every live shape on the frame in one op, so it is a single undo.
  const clearAll = useCallback(() => {
    if (!frameId || sync.role !== "worker") {
      return;
    }
    const ids = liveShapeIds(sync.opLog, frameId);
    if (ids.length === 0) {
      return;
    }
    submit({ frameId, kind: "ShapeDelete", payload: { shapeIds: ids }, undoOf: null, redoOf: null });
  }, [frameId, sync.role, sync.opLog, submit]);

  const undo = useCallback(() => {
    if (!frameId) {
      return;
    }
    const draft = makeUndo(sync.opLog, frameId, sync.localIdentity);
    if (draft) {
      submit(draft);
    }
  }, [frameId, sync.opLog, sync.localIdentity, submit]);

  const redo = useCallback(() => {
    if (!frameId) {
      return;
    }
    const draft = makeRedo(sync.opLog, frameId, sync.localIdentity);
    if (draft) {
      submit(draft);
    }
  }, [frameId, sync.opLog, sync.localIdentity, submit]);

  const history = frameId ? authorHistory(sync.opLog, frameId, sync.localIdentity) : { undo: [], redo: [] };

  const clearMessage = useCallback(() => setMessage(null), []);

  return {
    tool,
    setTool,
    color,
    setColor,
    width,
    setWidth,
    selectedShapeId,
    setSelectedShapeId,
    frameId,
    shapes,
    canDraw,
    canModify,
    create,
    move,
    restyleSelected,
    deleteSelected,
    clearAll,
    undo,
    redo,
    canUndo: history.undo.length > 0,
    canRedo: history.redo.length > 0,
    message,
    clearMessage,
  };
}

export default useAnnotationSession;
